import { h } from 'preact';

import { SCENE_CSS } from './keyframes';
import {
    layerTransform, DEPTH_SKY, DEPTH_FAR, DEPTH_MID, DEPTH_GROUND, DEPTH_HERO,
} from './parallax';
import { Cloud, GroundBand } from './sceneProps';

// A string of pennant flags hung between two poles, dipping in the middle.
const PennantString = ({ x1, x2, y = 66, dip = 14, palette }) => {
    const mid = (x1 + x2) / 2;
    const flags = [];
    const count = 6;
    for (let i = 1; i <= count; i += 1) {
        const t = i / (count + 1);
        // Point on the quadratic string curve at parameter t.
        const x = (1 - t) * (1 - t) * x1 + 2 * (1 - t) * t * mid + t * t * x2;
        const yy = (1 - t) * (1 - t) * y + 2 * (1 - t) * t * (y + dip) + t * t * y;
        flags.push(
            <polygon
                key={i}
                points={`${x - 5},${yy} ${x + 5},${yy} ${x},${yy + 11}`}
                fill={i % 2 === 0 ? palette.accent : palette.text}
                opacity='0.85'
            />,
        );
    }
    return (
        <g>
            <path d={`M${x1} ${y} Q ${mid} ${y + dip * 2} ${x2} ${y}`} fill='none' stroke={palette.mid} stroke-width='2' />
            <rect x={x1 - 2} y={y - 6} width='4' height={128 - y + 6} fill={palette.mid} />
            <rect x={x2 - 2} y={y - 6} width='4' height={128 - y + 6} fill={palette.mid} />
            {flags}
        </g>
    );
};

const CONFETTI = [
    { x: 330, delay: 0 }, { x: 355, delay: 0.08 }, { x: 380, delay: 0.16 },
    { x: 405, delay: 0.04 }, { x: 430, delay: 0.12 }, { x: 455, delay: 0.2 },
    { x: 470, delay: 0.06 }, { x: 345, delay: 0.14 },
];

const Runner = ({
    progress = 0, pastThreshold = false, released = false,
    pullOffset = 0, palette = {},
}) => {
    const driftSpeed = (18 - 10 * progress).toFixed(2);
    // Bob frequency speeds up with pull, mimicking faster strides.
    const bobSpeed = (0.5 - 0.3 * progress).toFixed(2);

    return (
        <div style={`position: relative; width: 100%; height: 100%; ${pastThreshold ? 'filter: brightness(1.1);' : ''}`}>
            <svg
                viewBox='0 0 800 150'
                preserveAspectRatio='xMidYMax slice'
                role='img'
                class='refresh-scene'
                style='position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible;'
            >
                <style>{SCENE_CSS}</style>
                <title>Dra ned for å oppdatere</title>

                {/* Sky */}
                <g style={layerTransform(pullOffset, DEPTH_SKY)}>
                    <Cloud x={140} y={20} s={0.9} fill={palette.sky} />
                    <Cloud x={430} y={14} s={0.7} fill={palette.sky} />
                    <Cloud x={660} y={24} fill={palette.sky} />
                </g>

                {/* Far — mountain ridge */}
                <g style={layerTransform(pullOffset, DEPTH_FAR)}>
                    <path d='M-100 150 L-100 104 L 40 70 L 140 100 L 260 62 L 380 102 L 500 74 L 620 104 L 730 82 L 900 106 L 900 150 Z' fill={palette.far} />
                </g>

                {/* Mid — pennant strings, drifting by */}
                <g style={layerTransform(pullOffset, DEPTH_MID)}>
                    <g class='drift' style={`animation-duration: ${driftSpeed}s;`}>
                        {[0, 800].map((off) => (
                            <g key={off} transform={`translate(${off} 0)`}>
                                <PennantString x1={80} x2={340} palette={palette} />
                                <PennantString x1={460} x2={720} palette={palette} />
                            </g>
                        ))}
                    </g>
                </g>

                {/* Ground — track with drifting lane dashes */}
                <g style={layerTransform(pullOffset, DEPTH_GROUND)}>
                    <GroundBand fill={palette.ground} />
                    <g class='drift' style={`animation-duration: ${(driftSpeed / 2.5).toFixed(2)}s;`}>
                        {[0, 800].map((off) => (
                            <g key={off} transform={`translate(${off} 0)`}>
                                {[30, 130, 230, 330, 430, 530, 630, 730].map((x) => (
                                    <line key={x} x1={x} y1='137' x2={x + 22} y2='137' stroke={palette.text} stroke-width='2.5' opacity='0.4' />
                                ))}
                            </g>
                        ))}
                    </g>
                </g>

                {/* Release — finish ribbon + confetti */}
                {released && (
                    <g>
                        <rect x='330' y='86' width='140' height='5' fill={palette.accent} style='animation: refreshRibbon 0.7s ease-out forwards;' />
                        {CONFETTI.map(({ x, delay }, idx) => (
                            <rect
                                key={x}
                                x={x}
                                y='40'
                                width='4'
                                height='7'
                                fill={idx % 2 === 0 ? palette.accent : palette.sky}
                                transform={`rotate(${(idx % 3) * 30} ${x} 40)`}
                            >
                                <animate attributeName='y' from='40' to='118' dur='0.9s' begin={`${delay}s`} fill='freeze' />
                                <animate attributeName='opacity' from='1' to='0' dur='0.9s' begin={`${delay}s`} fill='freeze' />
                            </rect>
                        ))}
                    </g>
                )}
            </svg>

            {/* Hero — runner striding on the track */}
            <div style={`position: absolute; inset: 0; display: flex; align-items: flex-end; justify-content: center; pointer-events: none; ${layerTransform(pullOffset, DEPTH_HERO)}`}>
                <i
                    class='fa-duotone fa-person-running'
                    style={`font-size: 76px; color: ${palette.text}; --fa-secondary-opacity: 0.5; padding-bottom: 24px; animation: refreshHeroBob ${bobSpeed}s ease-in-out infinite;`}
                />
            </div>
        </div>
    );
};

export default Runner;
